import {
  DEFAULT_WEAPON_STATS,
  type BranchOption,
  type PassiveDef,
  type StatusApply,
  type WeaponDef,
  type WeaponLevel,
  type WeaponStats,
} from '../core/types';
import { CONFIG } from '../config';

/** 패시브를 전부 합산한 플레이어 보정치. */
export interface PlayerStats {
  maxHp: number;
  moveSpeed: number;
  damageMul: number;
  cooldownMul: number; // 1보다 작을수록 빠름
  areaMul: number;
  projectileSpeedMul: number;
  pickupRadius: number;
  armor: number;
  regen: number;
  xpMul: number;
  luck: number;
  /** [견고] — 받는 피해 절대 감소. armor 와 합쳐서 쓴다. */
  tough: number;
  /** 부여하는 [화염] 중첩 추가 */
  burnBonus: number;
  /** 부여하는 [서리] 중첩 추가 */
  frostBonus: number;
  /** 캐릭터 특성: 베기(근접·거대한 검·잿빛대검) 피해 배율 */
  slashMul: number;
  /** 캐릭터 특성: 마법(그 밖 — 도약·장풍 제외) 피해 배율 */
  magicMul: number;
}

export interface OwnedPassive {
  def: PassiveDef;
  level: number;
}

export function computePlayerStats(
  baseHp: number,
  baseSpeed: number,
  passives: readonly OwnedPassive[],
): PlayerStats {
  const s: PlayerStats = {
    maxHp: baseHp,
    moveSpeed: baseSpeed,
    damageMul: 1,
    cooldownMul: 1,
    areaMul: 1,
    projectileSpeedMul: 1,
    pickupRadius: CONFIG.PICKUP_RADIUS,
    armor: 0,
    regen: 0,
    xpMul: 1,
    luck: 0,
    tough: 0,
    burnBonus: 0,
    frostBonus: 0,
    slashMul: 1,
    magicMul: 1,
  };

  let pickupMul = 1;
  for (const p of passives) {
    const m = p.def.mods;
    const n = p.level; // 레벨 수만큼 효과가 누적된다
    if (m.damageMul) s.damageMul += m.damageMul * n;
    if (m.moveSpeedMul) s.moveSpeed += baseSpeed * m.moveSpeedMul * n;
    if (m.cooldownMul) s.cooldownMul += m.cooldownMul * n;
    if (m.areaMul) s.areaMul += m.areaMul * n;
    if (m.projectileSpeedMul) s.projectileSpeedMul += m.projectileSpeedMul * n;
    if (m.pickupMul) pickupMul += m.pickupMul * n;
    if (m.xpMul) s.xpMul += m.xpMul * n;
    if (m.maxHpAdd) s.maxHp += m.maxHpAdd * n;
    if (m.armorAdd) s.armor += m.armorAdd * n;
    if (m.toughAdd) s.tough += m.toughAdd * n;
    if (m.burnAdd) s.burnBonus += m.burnAdd * n;
    if (m.frostAdd) s.frostBonus += m.frostAdd * n;
    if (m.regenAdd) s.regen += m.regenAdd * n;
    if (m.luckAdd) s.luck += m.luckAdd * n;
  }

  s.pickupRadius *= pickupMul;
  s.cooldownMul = Math.max(0.35, s.cooldownMul); // 쿨감 하한
  return s;
}

/**
 * 갈림길 한 곳에서 지금 고를 수 있는 갈래들. 앞 갈림길에서 고른 것(picks)에 따라 needs 가 맞는 것만 남긴다.
 * 아무것도 안 남으면(표를 잘못 적었을 때) 전부를 돌려준다.
 */
export function branchOptions(choose: readonly BranchOption[], picks: readonly string[]): BranchOption[] {
  const ok = choose.filter((o) => !o.needs || o.needs.some((id) => picks.includes(id)));
  return ok.length > 0 ? ok : [...choose];
}

/**
 * 지금 레벨까지 실제로 더해진 레벨 항목들.
 * 갈림길에서는 picks 에 적힌 갈래를 쓴다(갈림길마다 한 개씩, 순서대로). 안 골랐거나 못 고르는 갈래면 고를 수 있는 첫 갈래.
 */
function appliedSteps(def: WeaponDef, level: number, picks: readonly string[]): Array<WeaponLevel | BranchOption> {
  const out: Array<WeaponLevel | BranchOption> = [];
  let b = 0;
  const steps = Math.min(level - 1, def.levels.length);
  for (let i = 0; i < steps; i++) {
    const d = def.levels[i];
    if (d.choose?.length) {
      const opts = branchOptions(d.choose, picks.slice(0, b));
      const id = picks[b++];
      out.push(opts.find((o) => o.id === id) ?? opts[0]);
    } else {
      out.push(d);
    }
  }
  return out;
}

/** base + 지나온 레벨(고른 갈래 포함)을 더해 현재 무기 수치를 만든다. */
export function computeWeaponStats(def: WeaponDef, level: number, picks: readonly string[] = []): WeaponStats {
  const out: WeaponStats = { ...DEFAULT_WEAPON_STATS, ...def.base } as WeaponStats;
  for (const d of appliedSteps(def, level, picks)) {
    for (const key of Object.keys(d) as Array<keyof WeaponStats>) {
      const v = (d as Partial<WeaponStats>)[key];
      if (typeof v === 'number') out[key] = (out[key] ?? 0) + v;
    }
  }
  out.cooldown = Math.max(0.06, out.cooldown);
  out.count = Math.max(1, Math.round(out.count));
  out.volley = Math.max(1, Math.round(out.volley));
  out.charges = Math.max(1, Math.round(out.charges));
  return out;
}

/** base.status + 지나온 레벨의 status 를 누적한 현재 상태효과. */
export function computeWeaponStatus(def: WeaponDef, level: number, picks: readonly string[] = []): StatusApply {
  const out: StatusApply = { ...(def.status ?? {}) };
  for (const d of appliedSteps(def, level, picks)) {
    const st = d.status;
    if (!st) continue;
    for (const key of Object.keys(st) as Array<keyof StatusApply>) {
      out[key] = (out[key] ?? 0) + (st[key] ?? 0);
    }
  }
  return out;
}

/** 다음 레벨이 갈림길이면 거기서 고를 수 있는 갈래들(한 개일 수도 있다), 아니면 null. picks = 지금까지 고른 갈래 */
export function forkOptions(def: WeaponDef, level: number, picks: readonly string[]): BranchOption[] | null {
  const d = def.levels[level - 1];
  return d?.choose?.length ? branchOptions(d.choose, picks) : null;
}

/** 다음 레벨이 진화 갈림길(고를 갈래가 둘 이상)이면 그 갈래들, 아니면 null. */
export function nextBranch(def: WeaponDef, level: number, picks: readonly string[] = []): BranchOption[] | null {
  const opts = forkOptions(def, level, picks);
  return opts && opts.length >= 2 ? opts : null;
}

/** 다음 레벨업 설명 문구. 갈래가 하나뿐인 갈림길이면 그 갈래 이름과 설명. 없으면 빈 문자열. */
export function nextLevelDesc(def: WeaponDef, level: number, picks: readonly string[] = []): string {
  const opts = forkOptions(def, level, picks);
  if (opts?.length === 1) return `「${opts[0].name}」 ${opts[0]._desc}`;
  return def.levels[level - 1]?._desc ?? '';
}

/** 이 무기가 가질 수 있는 갈래 조합 전부 (측정용). 갈림길마다 한 개씩 — 고를 것 없는 갈림길의 갈래도 들어간다. 갈림길이 없으면 [[]] */
export function allPaths(def: WeaponDef): string[][] {
  let out: string[][] = [[]];
  for (const d of def.levels) {
    if (!d.choose?.length) continue;
    const next: string[][] = [];
    for (const p of out) for (const o of branchOptions(d.choose, p)) next.push([...p, o.id]);
    out = next;
  }
  return out;
}
